/**
 * Время доставки в два клика (владелец 26.10.26: «день, с, по — разные поля, много кликать; сделай,
 * чтобы в одно заполнение»). Одно поле — один попап:
 *
 * - `WindowPick` — окно у клиента: день плиткой (две недели вокруг дня проката), окно готовым
 *   «10–12» одним кликом, своё — двумя кликами по часам (начало, конец). Готовое окно закрывает попап.
 * - `HourPick` — «забрать из салона / вернуть в салон не позднее»: готовое «за 1 ч до окна» одним
 *   кликом или день + час.
 *
 * Время — салона, целыми часами (как окно на сервере). Стиль кнопки и попапа — как `DateTimePick`.
 */
import { ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";
import { useEffect, useRef, useState } from "react";

const WEEKDAYS = ["вс", "пн", "вт", "ср", "чт", "пт", "сб"];
const PRESETS: [number, number][] = [
  [10, 12],
  [12, 14],
  [14, 16],
  [16, 18],
  [18, 20],
  [10, 14],
  [14, 18],
];
const HOURS = Array.from({ length: 16 }, (_, i) => i + 7); // 07–22
const hh = (h: number) => `${String(h).padStart(2, "0")}:00`;

const parse = (day: string) => new Date(Number(day.slice(0, 4)), Number(day.slice(5, 7)) - 1, Number(day.slice(8, 10)));
const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const shift = (day: string, n: number) => {
  const d = parse(day);
  d.setDate(d.getDate() + n);
  return iso(d);
};
const today = () => iso(new Date());

/** `2026-10-17` → «пт 17.10». */
export const dayShort = (day: string) => (day ? `${WEEKDAYS[parse(day).getDay()]} ${day.slice(8, 10)}.${day.slice(5, 7)}` : "");

/** Попап под полем: клик мимо и Escape закрывают без изменений. */
function usePopover() {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    const escape = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", away);
    document.addEventListener("keydown", escape);
    return () => {
      document.removeEventListener("mousedown", away);
      document.removeEventListener("keydown", escape);
    };
  }, [open]);
  return { open, setOpen, ref };
}

function Field({ label, text, placeholder, open, onClick, bad }: { label: string; text: string; placeholder: string; open: boolean; onClick: () => void; bad?: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex h-9 w-full items-center overflow-hidden rounded-[var(--radius-ui)] border bg-bg text-[13px] ${bad ? "border-err" : open ? "border-border-strong" : "border-border hover:border-border-strong"}`}
    >
      <span className="flex h-full flex-none items-center whitespace-nowrap border-r border-border bg-bg-subtle px-2 text-[11.5px] text-fg-faint">{label}</span>
      <span className={`truncate px-2 tabular-nums ${bad ? "text-err" : text ? "text-fg" : "text-fg-faint"}`}>{text || placeholder}</span>
      <ChevronDown size={13} className="mr-1.5 ml-auto flex-none text-fg-faint" />
    </button>
  );
}

const popup = "absolute left-0 right-0 top-[40px] z-30 grid gap-2 rounded-[var(--radius-card)] border border-border bg-bg-elevated p-2 shadow-lg";
const chip = (on: boolean) =>
  `rounded-[var(--radius-ui)] border px-1 py-1 text-center text-[12px] tabular-nums ${on ? "border-accent bg-accent text-white" : "border-border bg-bg hover:bg-bg-hover"}`;
const caption = "text-[11px] text-fg-faint";

/** Две недели плитками, стрелки — на неделю; день проката подчёркнут. */
function Days({ day, anchor, onPick }: { day: string; anchor: string; onPick: (d: string) => void }) {
  const [start, setStart] = useState(() => shift(day || anchor || today(), -3));
  const days = Array.from({ length: 14 }, (_, i) => shift(start, i));
  return (
    <div className="grid gap-1">
      <div className="flex items-center justify-between">
        <span className={caption}>День</span>
        <span className="flex gap-1">
          <button type="button" className="rounded p-0.5 text-fg-muted hover:bg-bg-hover" aria-label="Неделя назад" onClick={() => setStart(shift(start, -7))}>
            <ChevronLeft size={14} />
          </button>
          <button type="button" className="rounded p-0.5 text-fg-muted hover:bg-bg-hover" aria-label="Неделя вперёд" onClick={() => setStart(shift(start, 7))}>
            <ChevronRight size={14} />
          </button>
        </span>
      </div>
      <div className="grid grid-cols-7 gap-1">
        {days.map((d) => {
          const w = parse(d).getDay();
          return (
            <button key={d} type="button" onClick={() => onPick(d)} className={`${chip(d === day)} grid leading-tight ${d === anchor && d !== day ? "underline decoration-accent underline-offset-2" : ""}`}>
              <span className={`text-[10px] ${d === day ? "" : w === 0 || w === 6 ? "text-warn" : "text-fg-faint"}`}>{WEEKDAYS[w]}</span>
              <span>{d.slice(8, 10)}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/** Окно у клиента: день + готовое окно одним кликом, своё — начало и конец по часам. */
export function WindowPick({
  label,
  day,
  from,
  to,
  anchor,
  bad = false,
  onChange,
}: {
  label: string;
  day: string;
  /** Ошибка в окне — рамка красным. */
  bad?: boolean;
  /** −1 — начало не задано. */
  from: number;
  to: number;
  /** День проката: с него открываются плитки. */
  anchor: string;
  onChange: (day: string, from: number, to: number) => void;
}) {
  const { open, setOpen, ref } = usePopover();
  const [d, setD] = useState(day);
  const [start, setStart] = useState<number | null>(null);
  useEffect(() => {
    if (open) {
      setD(day);
      setStart(null);
    }
  }, [open, day]);
  const apply = (f: number, t: number) => {
    onChange(d || anchor || today(), f, t);
    setOpen(false);
  };
  const pickHour = (h: number) => {
    if (start === null || h <= start) return setStart(h);
    apply(start, h);
  };
  const text = day ? `${dayShort(day)} · ${from >= 0 ? `${hh(from)}–${hh(to)}` : `до ${hh(to)} · окно не выбрано`}` : "";
  return (
    <div ref={ref} className="relative" data-annot={`окно: ${label}`}>
      <Field label={label} text={text} placeholder="выберите день и окно" open={open} bad={bad} onClick={() => setOpen((v) => !v)} />
      {open && (
        <div className={popup}>
          <Days day={d} anchor={anchor} onPick={setD} />
          <div className="grid gap-1">
            <span className={caption}>Окно — один клик</span>
            <div className="grid grid-cols-4 gap-1">
              {PRESETS.map(([f, t]) => (
                <button key={`${f}-${t}`} type="button" className={chip(d === day && f === from && t === to)} onClick={() => apply(f, t)}>
                  {f}–{t}
                </button>
              ))}
            </div>
          </div>
          <div className="grid gap-1">
            <span className={caption}>{start === null ? "Своё окно — нажмите начало, потом конец" : `С ${hh(start)} — теперь конец окна`}</span>
            <div className="grid grid-cols-8 gap-1">
              {HOURS.map((h) => {
                const inRange = start !== null ? h === start : d === day && from >= 0 && h >= from && h <= to;
                return (
                  <button key={h} type="button" className={chip(inRange)} onClick={() => pickHour(h)}>
                    {h}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/** «Не позднее» одним кликом: подсказки от окна или день + час. Пусто — срок не задан. */
export function HourPick({
  label,
  value,
  anchor,
  suggest,
  bad = false,
  onChange,
}: {
  bad?: boolean;
  label: string;
  /** `ГГГГ-ММ-ДДTЧЧ:ММ` или пусто. */
  value: string;
  anchor: string;
  suggest: { label: string; value: string }[];
  onChange: (value: string) => void;
}) {
  const { open, setOpen, ref } = usePopover();
  const [d, setD] = useState(value.slice(0, 10) || anchor);
  useEffect(() => {
    if (open) setD(value.slice(0, 10) || anchor);
  }, [open, value, anchor]);
  const apply = (v: string) => {
    onChange(v);
    setOpen(false);
  };
  const text = value ? `${dayShort(value.slice(0, 10))} · ${value.slice(11, 16)}` : "";
  const hour = value && value.slice(0, 10) === d ? Number(value.slice(11, 13)) : -1;
  return (
    <div ref={ref} className="relative" data-annot={`срок: ${label}`}>
      <Field label={label} text={text} placeholder="не задано" open={open} bad={bad} onClick={() => setOpen((v) => !v)} />
      {open && (
        <div className={popup}>
          {suggest.length > 0 && (
            <div className="grid gap-1">
              <span className={caption}>Один клик</span>
              <div className="flex flex-wrap gap-1">
                {suggest.map((s) => (
                  <button key={s.label} type="button" className={`${chip(s.value === value)} px-2`} onClick={() => apply(s.value)}>
                    {s.label} · {s.value.slice(11, 16)}
                  </button>
                ))}
              </div>
            </div>
          )}
          <Days day={d} anchor={anchor} onPick={setD} />
          <div className="grid gap-1">
            <span className={caption}>Час</span>
            <div className="grid grid-cols-8 gap-1">
              {HOURS.map((h) => (
                <button key={h} type="button" className={chip(h === hour)} onClick={() => apply(`${d}T${hh(h)}`)}>
                  {h}
                </button>
              ))}
            </div>
          </div>
          {value && (
            <button type="button" className="justify-self-end text-[11.5px] text-fg-faint hover:text-err" onClick={() => apply("")}>
              очистить
            </button>
          )}
        </div>
      )}
    </div>
  );
}
